import { api } from './client';
import type { StrategyDocumentDto } from './types';

export type TradingEnvironment = 'Paper' | 'Demo' | 'Real';

export interface TradingGatewayDto {
  id: string; name: string; enabled: boolean; connected: boolean; lastSeenUtc: string | null;
  brokerName: string | null; accountId: string | null; accountEnvironment: TradingEnvironment | null;
  positionMode: string | null; health: 'Connected' | 'Offline' | 'Revoked';
  expectedEnvironment: TradingEnvironment | null; expectedBrokerName: string | null; expectedAccountId: string | null;
  realTradingArmed: boolean; emergencyStopLatched: boolean;
  supportsLiveQuotes: boolean; supportsRemoteLogs: boolean; symbols: TradingSymbol[];
}

export interface TradingAccountSnapshot {
  accountId: string; environment: TradingEnvironment; positionMode: 'Netting' | 'Hedging'; currency: string;
  balance: number; equity: number; margin: number; freeMargin: number; asOfUtc: string;
}

export interface TradingSymbol {
  executionConstraints?: { tradeMode: string; supportsStopLoss: boolean; supportsTakeProfit: boolean; minimumStopDistance: number; freezeDistance: number; marketTimeInForce: string[]; pendingTimeInForce: string[]; supportsSpecifiedExpiration: boolean };
  symbol: string; volumeUnit: string; tickSize: number; minimumVolume: number; maximumVolume: number;
  volumeStep: number; supportedOrderTypes: string[];
}

export interface TradingQuote {
  symbol: string; timeUtc: string; bid: number; ask: number; last: number | null; availableVolume: number | null;
}

export interface LiveQuoteSnapshot {
  gatewayId: string; quote: TradingQuote; receivedUtc: string;
}

export interface TradingAdapterCapabilities {
  supportsLiveQuotes: boolean;
  [key: string]: unknown;
}

export interface TradingSymbolRiskLimitDto {
  symbol: string; maxOrderVolume: number; maxOpenVolume: number; maxOrderNotional: number;
  maxPendingPriceDistanceBps: number;
}

export interface TradingRiskProfileDto {
  maxOpenPositions: number; maxOrdersPerMinute: number; maxDailyRealizedLoss: number;
  maxQuoteAgeSeconds: number; maxMarketSlippageBps: number; symbols: TradingSymbolRiskLimitDto[];
}

export interface TradingGatewayDetailDto extends TradingGatewayDto {
  runtimeMetadata?: TradingRuntimeMetadata | null;
  agentVersion: string | null; realTradingArmedUtc: string | null; emergencyStopUtc: string | null;
  riskProfile: TradingRiskProfileDto | null; account: TradingAccountSnapshot | null;
  capabilities: TradingAdapterCapabilities | null; symbols: TradingSymbol[];
  latestFault: { occurredUtc: string; code: string; message: string; isFatal: boolean } | null;
}

export interface TradingRuntimeMetadata {
  gatewayVersion: string; eaVersion?: string | null; terminalBuild?: string | null;
  brokerTimeZoneId?: string | null; brokerUtcOffsetMinutes?: number | null; brokerTimeZoneShiftMinutes?: number | null;
}

export interface CreatedGatewayDto { id: string; name: string; apiKey: string }
export interface CreateGatewayRequest {
  name: string; expectedEnvironment?: TradingEnvironment; expectedBrokerName?: string; expectedAccountId?: string;
}

export interface TradingDeploymentDto {
  kind?: 'Strategy' | 'Manual' | 'Diagnostic'; interventionPaused?: boolean;
  id: string; name: string; gatewayId: string; status: string; activationId: string | null; warmupDays: number;
  symbols: string[]; createdUtc: string; updatedUtc: string; startedUtc: string | null; activatedUtc: string | null; stoppedUtc: string | null;
  forceStoppedUtc: string | null; retiredUtc: string | null; retirementForced: boolean; retirementReason: string | null;
  error: string | null; strategyName: string; strategySnapshotHash: string; gatewayName: string;
  accountEnvironment: TradingEnvironment | null; brokerName: string | null; accountId: string | null;
  symbolHealth: TradingDeploymentSymbolHealthDto[];
  positionCount: number; workingOrderCount: number; unrealizedPnl: number; realizedPnl: number;
}

export type TradingPipelineHealth = 'NotActive' | 'Offline' | 'Paused' | 'WaitingForFirstBar' | 'MarketInactive' | 'Healthy' | 'Stalled';
export interface TradingDeploymentSymbolHealthDto {
  symbol: string; barDurationSeconds: number;
  subscriptionCommandId: string | null; subscriptionRequestedUtc: string | null; subscriptionAcknowledgedUtc: string | null;
  lastBarOpenUtc: string | null; marketDataThroughUtc: string | null;
  lastBarReceivedUtc: string | null; lastBarProcessedUtc: string | null;
  liveBarsReceived: number; liveBarsProcessed: number; evaluationsCompleted: number; lastEvaluationUtc: string | null;
  intentsProduced: number; commandsQueued: number; intentsSuppressed: number;
  lastEvaluationIntentCount: number; lastIntentDisposition: string | null;
  health: TradingPipelineHealth; detail: string;
}

export interface CreateTradingDeploymentRequest {
  name: string; gatewayId: string; strategyDocument: StrategyDocumentDto; symbols: string[]; warmupDays: number;
  maxPositionsPerDirection?: number;
}

export type TradingInputKind = 'Number' | 'Boolean' | 'Direction' | 'Text';

export interface TradingInputOverrideDto {
  symbol: string; valueJson: string; updatedUtc: string; updatedByUserId: string | null;
}

export interface TradingInputDto {
  key: string; kind: TradingInputKind; label: string | null; defaultValueJson: string | null;
  overrides: TradingInputOverrideDto[];
}

export interface TradingDeploymentInputsDto {
  maxPositionsPerDirection: number; symbols: string[]; inputs: TradingInputDto[];
}

export interface TradingInputEntryDto {
  key: string; kind: string; valueJson: string; symbol: string | null;
}

export interface SaveTradingDeploymentInputsRequest {
  entries: TradingInputEntryDto[];
}

export interface TradingOrder {
  brokerOrderId: string; commandId: string; deploymentId: string; activationId: string; intentId: string;
  symbol: string; side: string; orderType: string; state: string; requestedVolume: number; filledVolume: number;
  limitPrice: number | null; stopPrice: number | null; stopLoss: number | null; takeProfit: number | null;
  createdUtc: string; updatedUtc: string; rejectionReason: string | null;
}
export interface TradingPosition {
  brokerPositionId: string; deploymentId: string; activationId: string; symbol: string; side: string; volume: number;
  entryPrice: number; currentPrice: number; stopLoss: number | null; takeProfit: number | null;
  unrealizedPnl: number; openedUtc: string; updatedUtc: string;
}
export interface TradingDeal {
  brokerDealId: string; brokerOrderId: string; brokerPositionId: string | null; deploymentId: string;
  activationId: string; symbol: string; side: string; volume: number; price: number; commission: number;
  realizedPnl: number; timeUtc: string; entry: 'Unknown' | 'Open' | 'Close' | 'Reverse' | 'CloseBy';
}
export interface TradingActivityDto {
  id: string; source: string; type: string; outcome: string | null; detail: string | null;
  actor: string | null; occurredUtc: string; payloadJson: string | null;
}
export interface TradingExposureItemDto {
  kind: 'Order' | 'Position'; brokerId: string; symbol: string;
  ownership: 'Owned' | 'Foreign' | 'Ambiguous'; deploymentId: string | null;
}
export interface TradingDeploymentExposureDto {
  owned: TradingExposureItemDto[]; foreign: TradingExposureItemDto[]; ambiguous: TradingExposureItemDto[];
}
export interface TradingPreflightDto {
  canStart: boolean; requiresLiveAcknowledgement: boolean;
  checks: { code: string; label: string; passed: boolean; detail: string }[];
  evidence: { kind: string; id: string; label: string; detail: string; timeUtc: string | null }[];
}
export interface TradingOverviewDto {
  connectedAccounts: number; activeDeployments: number; openPositions: number; attentionRequired: number;
  accounts: TradingGatewayDto[]; deployments: TradingDeploymentDto[];
}
export interface TradingCommandDto { id: string; type: string; status: string; payloadJson: string; createdUtc: string; error: string | null }
export interface TradingAuditEventDto { id: number; type: string; payloadJson: string; occurredUtc: string; receivedUtc: string }

const deploymentPath = (id: string) => `/api/trading/deployments/${encodeURIComponent(id)}`;
const gatewayPath = (id: string) => `/api/trading/gateways/${encodeURIComponent(id)}`;

export const tradingApi = {
  overview: (includeArchived = false, signal?: AbortSignal) => api.get<TradingOverviewDto>(`/api/trading/overview${includeArchived ? '?includeArchived=true' : ''}`, { signal }),
  quotes: (signal?: AbortSignal) => api.get<LiveQuoteSnapshot[]>('/api/trading/quotes', { signal }),
  gateways: (signal?: AbortSignal) => api.get<TradingGatewayDto[]>('/api/trading/gateways', { signal }),
  gateway: (id: string, signal?: AbortSignal) => api.get<TradingGatewayDetailDto>(gatewayPath(id), { signal }),
  gatewayQuotes: (id: string, signal?: AbortSignal) => api.get<LiveQuoteSnapshot[]>(`${gatewayPath(id)}/quotes`, { signal }),
  createGateway: (request: CreateGatewayRequest) => api.post<CreatedGatewayDto>('/api/trading/gateways', request),
  rotateGatewayKey: (id: string) => api.post<CreatedGatewayDto>(`${gatewayPath(id)}/rotate-key`),
  revokeGateway: (id: string) => api.post<void>(`${gatewayPath(id)}/revoke`),
  updateRiskProfile: (id: string, profile: TradingRiskProfileDto) => api.put<TradingGatewayDetailDto>(`${gatewayPath(id)}/risk-profile`, profile),
  gatewayAction: (id: string, action: 'arm' | 'disarm' | 'emergency-stop' | 'clear-emergency-stop' | 'flatten') => api.post<void | TradingGatewayDetailDto>(`${gatewayPath(id)}/${action}`),
  emergencyStopAll: () => api.post<void>('/api/trading/emergency-stop'),
  deployments: (includeArchived = false, signal?: AbortSignal) => api.get<TradingDeploymentDto[]>(`/api/trading/deployments${includeArchived ? '?includeArchived=true' : ''}`, { signal }),
  deployment: (id: string, signal?: AbortSignal) => api.get<TradingDeploymentDto>(deploymentPath(id), { signal }),
  getDeploymentInputs: (id: string, signal?: AbortSignal) => api.get<TradingDeploymentInputsDto>(`${deploymentPath(id)}/inputs`, { signal }),
  saveDeploymentInputs: (id: string, request: SaveTradingDeploymentInputsRequest) => api.put<void>(`${deploymentPath(id)}/inputs`, request),
  preflight: (id: string, signal?: AbortSignal) => api.get<TradingPreflightDto>(`${deploymentPath(id)}/preflight`, { signal }),
  createDeployment: (request: CreateTradingDeploymentRequest) => api.post<TradingDeploymentDto>('/api/trading/deployments', request),
  action: (id: string, action: 'start' | 'restart' | 'stop' | 'flatten', request?: { expectedAccountId?: string; acknowledgement?: string }) => api.post<void>(`${deploymentPath(id)}/${action}`, request),
  forceStop: (id: string, request: { acknowledgement: string; reason: string }) => api.post<void>(`${deploymentPath(id)}/force-stop`, request),
  retire: (id: string, request: { forceRelease: boolean; acknowledgement?: string; reason?: string }) => api.post<void>(`${deploymentPath(id)}/retire`, request),
  orders: (id: string, signal?: AbortSignal) => api.get<TradingOrder[]>(`${deploymentPath(id)}/orders`, { signal }),
  positions: (id: string, signal?: AbortSignal) => api.get<TradingPosition[]>(`${deploymentPath(id)}/positions`, { signal }),
  exposure: (id: string, signal?: AbortSignal) => api.get<TradingDeploymentExposureDto>(`${deploymentPath(id)}/exposure`, { signal }),
  deals: (id: string, signal?: AbortSignal) => api.get<TradingDeal[]>(`${deploymentPath(id)}/deals?limit=100`, { signal }),
  activity: (id: string, signal?: AbortSignal, beforeId?: string) => api.get<TradingActivityDto[]>(`${deploymentPath(id)}/activity?limit=100${beforeId ? `&beforeId=${encodeURIComponent(beforeId)}` : ''}`, { signal }),
  commands: (id: string, signal?: AbortSignal) => api.get<TradingCommandDto[]>(`${deploymentPath(id)}/commands`, { signal }),
  events: (id: string, signal?: AbortSignal) => api.get<TradingAuditEventDto[]>(`${deploymentPath(id)}/events`, { signal }),
};
